import { memo, useState } from "react";
import { Link } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Unstable_Grid2";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";
import { useUserStore } from "stores/UserStore";

import { isValidEmail } from "utility/is_valid_email";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

const ConfigureGoogleSheet = memo(function ConfigureGoogleSheet() {
  const [userStore] = useUserStore();
  const [, actions] = useConnectionStore();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("ThreatKey Findings");
  const [email, setEmail] = useState(userStore.user.email);
  const [driveURL, setDriveURL] = useState<string>();
  const emailError = isValidEmail(email) ? "" : "Not a valid email address";
  const [responseTitleError, setResponseTitleError] = useState("");

  let titleError = "";
  if (responseTitleError) {
    titleError = responseTitleError;
  } else if (title === "") {
    titleError = "Google Drive filenames can be anything but empty";
  }

  const canSubmit = !titleError && !emailError;

  const connect = () => {
    if (!loading) {
      setLoading(true);
      actions.googleSheetConnect({
        title,
        email,
        onSuccess: ({ url }) => {
          setLoading(false);
          setDriveURL(url);
        },
        onError: (response) => {
          setLoading(false);
          if (response.title) {
            setResponseTitleError(response.title);
          }
        },
      });
    }
  };
  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/destinations"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/destinations"
        >
          Destinations
        </Breadcrumb>
        <span />
      </Breadcrumbs>
      <Stack spacing={2}>
        <ServiceConnectionInstructions label="Google Sheet Sync">
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              How Google Sheet Sync works:
            </Typography>
            <Typography variant="body1">
              Google Sheet Sync allows the ThreatKey platform to
              &quot;connect&quot; to a Google Sheet, synchronizing all findings
              periodically into a spreadsheet. Although released on April 1,
              this integration is engineered and considered no less than other
              parts of ThreatKey&apos;s feature set.
            </Typography>
            <Typography variant="body1">
              We recognize that effective security and vulnerability management
              programs do not necessarily need exciting, elaborate software to
              service immediate management, planning, and reporting needs. In
              many instances, you really can get the job done with a spreadsheet
              and reliable data.
            </Typography>
          </Stack>
        </ServiceConnectionInstructions>
        <ServiceConnectionStep disabled={loading || !!driveURL}>
          <Typography variant="body1">
            Some automation on our side will create a Google Sheet - please
            enter a title for the spreadsheet and what email it will be shared
            to.
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Google Sheet title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  if (responseTitleError) {
                    setResponseTitleError("");
                  }
                }}
                sx={{ mt: 1 }}
                error={!!titleError}
                helperText={titleError}
              />
            </Grid2>
            <Grid2 xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                sx={{ mt: 1 }}
                error={!!emailError}
                helperText={emailError}
              />
            </Grid2>
            <Grid2
              xs={12}
              display="flex"
              justifyContent="right"
              alignItems="flex-end"
            >
              <LoadingButton
                variant="contained"
                type="submit"
                onClick={connect}
                loading={loading}
                disabled={!canSubmit}
              >
                Create and share sheet
              </LoadingButton>
            </Grid2>
          </Grid2>
        </ServiceConnectionStep>
        {!driveURL ? null : (
          <ServiceConnectionStep disabled={!driveURL}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">Done!</Typography>
            </Box>
            <Stack sx={{ mt: 2 }} spacing={1}>
              <Typography variant="body1">
                <a href={driveURL}>{title}</a> has been created and shared to{" "}
                {email}. If you click the link fast enough, you might be able to
                catch our automation filling out the sheet!
              </Typography>
            </Stack>
          </ServiceConnectionStep>
        )}
      </Stack>
    </>
  );
});
export default ConfigureGoogleSheet;
